import { Injectable } from '@angular/core';
import {
  Action,
  NgxsAfterBootstrap,
  State,
  StateContext,
  Store,
} from '@ngxs/store';
import { Assessment } from '../../../../types/assessment';
import { ASSESSMENT_LIST_STATE } from '../../../../types/state-name';
import {
  AddAssessmentAction,
  FetchAssessmentListAction,
} from '../actions/assessment-list.action';
import { map, retry, tap, switchMap } from 'rxjs';
import { AssessmentServiceNew } from '../../../../../sdk/src/lib/services/assessment-new.service';
import { Auth0Service } from '../../../../../sdk/src/lib/services/auth0.service';
import { UserProfileService } from '../../../../../sdk/src/lib/services/user-profile.service';

@State<Array<Assessment>>({
  name: ASSESSMENT_LIST_STATE,
  defaults: [],
})
@Injectable({
  providedIn: 'root',
})
export class AssessmentListState implements NgxsAfterBootstrap {
  constructor(
    private _store: Store,
    private _assessmentService: AssessmentServiceNew,
    private _authService: Auth0Service,
    private _userProfileService: UserProfileService
  ) {}

  ngxsAfterBootstrap(stateContext: StateContext<Array<Assessment>>) {
    this._authService.isAuthenticated$
      .pipe(
        tap((isAuthenticated) => {
          if (isAuthenticated) {
            this._authService.user$
              .pipe(
                tap((user) => {
                  if (user?.sub) {
                    this._store.dispatch(
                      new FetchAssessmentListAction(user.sub)
                    );
                  }
                })
              )
              .subscribe();
          }
        })
      )
      .subscribe();
  }

  static getAssessmentState(state: any) {
    return state[ASSESSMENT_LIST_STATE];
  }

  @Action(FetchAssessmentListAction)
  fetchAssessmentList(
    stateContext: StateContext<Array<Assessment>>,
    fetchAssessmentListAction: FetchAssessmentListAction
  ) {
    // Use provided user ID
    return this._userProfileService
      .getUserProfile(fetchAssessmentListAction.userId!)
      .pipe(
        switchMap((userProfile) => {
          const therapistId = userProfile?.auth_zero_user_id;
          if (!therapistId) {
            throw new Error('No therapist ID found in user profile');
          }
          return this._assessmentService.getAssessmentListByTherapist(
            therapistId
          );
        }),
        retry(3),
        map((assessmentList) => {
          return stateContext.setState([...assessmentList]);
        })
      );
  }

  @Action(AddAssessmentAction)
  addAssessment(
    stateContext: StateContext<Array<Assessment>>,
    addAssessmentAction: AddAssessmentAction
  ) {
    return this._assessmentService
      .updateAssessment(addAssessmentAction.assessment)
      .pipe(
        map(() => {
          const ASSESSMENT_LIST = [...stateContext.getState()];
          const FILTERED_LIST = ASSESSMENT_LIST.filter(
            (assessment) =>
              assessment?._id.toString() !==
              addAssessmentAction?.assessment?._id.toString()
          );
          const UPDATED_LIST = FILTERED_LIST.concat(
            addAssessmentAction.assessment
          );

          return stateContext.setState(UPDATED_LIST);
        })
      );
  }
}
